/**
 * Home Page Component
 * Main landing page with sequencer, controls, and features
 */

import { useState, useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/common/SEO';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { InsufficientCreditsModal } from '../components/common/InsufficientCreditsModal';
import { SaveCompositionDialog } from '../components/common/SaveCompositionDialog';
import { CompositionLimitModal } from '../components/common/CompositionLimitModal';
import { PurchaseStatusHandler } from '../components/common/PurchaseStatusHandler';
import { QuickFacts } from '../components/common/QuickFacts';
import { DiscordInvitePopup } from '../components/common/DiscordInvitePopup';
import { PlaybackControls } from '../components/controls/PlaybackControls';
import { GenerateControls } from '../components/controls/GenerateControls';
import { SequencerGrid } from '../components/grid/SequencerGrid';
import { TemplateButton } from '../components/templates/TemplateButton';
import { useGrid } from '../hooks/useGrid';
import { useTone } from '../hooks/useTone';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { useAnalytics } from '../hooks/useAnalytics';
import { useDeepLink } from '../hooks/useDeepLink';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useMobileState } from '../hooks/useMobileState';
import { shareAPI } from '../services/api/ShareAPI';
import { compositionAPI } from '../services/api/CompositionAPI';
import { generateAPI } from '../services/api/GenerateAPI';
import { storageService } from '../services/storage/StorageService';
import { activityTrackingService } from '../services/user/ActivityTrackingService';
import { sessionManager } from '../services/user/SessionManager';
import { deepLinkHandler } from '../services/deepLink';
import { APIServiceError } from '../services/api/APIService';
import { formatErrorMessage, OPERATION_ERROR_MESSAGES } from '../utils/errorMessages';
import type { Composition } from '../types/composition';
import type { Template } from '../types/template';
import {
  MobileComposerLayout,
  TopTransportBar,
  TrackHeader,
  StepGrid,
  BottomTabs,
  TracksPanel,
  SoundPanel,
  SongSettingsPanel,
  VolumeBottomSheet,
  BPMPickerModal,
  StepsPickerModal,
  TrackActionsMenu,
  FooterButton,
  FooterDrawer,
  AIGeneratePanel,
  ShareLinkModal,
} from '../components/mobile';

// Lazy load HelpModal for code splitting
const HelpModal = lazy(() => import('../components/controls/HelpModal').then(module => ({ default: module.HelpModal })));

// Lazy load TemplateBrowser for code splitting
const TemplateBrowser = lazy(() => import('../components/templates/TemplateBrowser').then(module => ({ default: module.TemplateBrowser })));

// Development mode flag - set to true to bypass authentication for local testing
const DEV_MODE = import.meta.env.DEV;
const DEV_MOCK_LIMIT_ERROR = false; // Set to true to test the limit modal

interface HomeProps {
  sharedComposition?: Composition | null;
}

/**
 * Home page component
 * Renders the main application interface
 */
export function Home({ sharedComposition }: HomeProps) {
  const [shareStatus, setShareStatus] = useState('');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isTemplateBrowserOpen, setIsTemplateBrowserOpen] = useState(false);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<Template | null>(null);
  const [saveIndicator, setSaveIndicator] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [insufficientCreditsInfo, setInsufficientCreditsInfo] = useState({ required: 0, available: 0 });
  
  // Save composition state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentCompositionId, setCurrentCompositionId] = useState<string | null>(null);
  
  // AI generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');
  const [generationError, setGenerationError] = useState('');
  
  // Mobile footer drawer state
  const [isFooterDrawerOpen, setIsFooterDrawerOpen] = useState(false);
  const [compositionTitle, setCompositionTitle] = useState<string>('Untitled');
  
  // Composition limit modal state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [compositionCount, setCompositionCount] = useState(0);
  
  // Share link modal state
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGeneratingShareLink, setIsGeneratingShareLink] = useState(false);
  const [shareLinkError, setShareLinkError] = useState('');
  
  // Track initial composition to detect changes
  const initialCompositionRef = useRef<string>('');
  
  // Track if manual composition creation has been tracked in this session
  const manualComposeTrackedRef = useRef<boolean>(false);
  
  // Track flipped feature cards for easter egg
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());
  const isInitialLoadRef = useRef(true);
  const templateButtonRef = useRef<HTMLButtonElement>(null);
  const hasShownRestorationNotification = useRef(false);
  const saveIndicatorTimeoutRef = useRef<number | null>(null);

  /**
   * Handle storage errors from useGrid hook
   */
  const handleStorageError = useCallback((error: {
    type: 'save' | 'load';
    message: string;
    errorType?: string;
  }) => {
    console.error('[Home] Storage error:', error);
    
    let errorMessage = '';
    
    if (error.type === 'save') {
      if (error.errorType === 'QUOTA_EXCEEDED') {
        errorMessage = '⚠️ Storage full! Your composition cannot be saved. Try clearing browser data or exporting your work.';
      } else {
        errorMessage = '⚠️ Failed to save composition. Your changes may not persist.';
      }
    } else if (error.type === 'load') {
      if (error.errorType === 'PARSE_FAILED') {
        errorMessage = '⚠️ Saved composition was corrupted. Starting with a fresh slate.';
      } else {
        errorMessage = '⚠️ Failed to load saved composition. Starting fresh.';
      }
    }
    
    setShareStatus(errorMessage);
    setTimeout(() => setShareStatus(''), 7000);
  }, []);

  /**
   * Handle storage success from useGrid hook
   */
  const handleStorageSuccess = useCallback((event: { type: 'save' }) => {
    if (event.type === 'save') {
      // Clear any existing timeout
      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }
      
      // Show save indicator
      setSaveIndicator('saved');
      
      // Hide after 2 seconds
      saveIndicatorTimeoutRef.current = window.setTimeout(() => {
        setSaveIndicator('');
        saveIndicatorTimeoutRef.current = null;
      }, 2000);
    }
  }, []);

  // Get analytics tracking (MUST be declared before any callbacks that use it)
  const analytics = useAnalytics();

  // Get grid state and actions
  const {
    gridState,
    noteState,
    volumeState,
    muted,
    instrumentNames,
    parametersEnabled,
    effectsChains,
    effectsPanelExpanded,
    steps,
    bpm,
    currentStep,
    toggleCell: toggleCellBase,
    setNote: setNoteBase,
    setInstrument: setInstrumentBase,
    updateInstrumentParameters,
    getInstrumentParameters,
    toggleParametersEnabled: toggleParametersEnabledBase,
    setVolume,
    toggleMute,
    addRow: addRowBase,
    removeRow: removeRowBase,
    setSteps: setStepsBase,
    setBpm: setBpmBase,
    setCurrentStep,
    clearGrid,
    resetGrid,
    loadComposition,
    getComposition,
    setEffectsChain,
    toggleEffectsPanel,
  } = useGrid(true, handleStorageError, handleStorageSuccess);
  
  // Wrap grid actions with analytics tracking
  const toggleCell = useCallback((row: number, step: number) => {
    const wasActive = gridState[row]?.[step];
    
    // Check if grid is empty before toggling (to detect manual composition start)
    const isGridEmpty = !manualComposeTrackedRef.current && gridState.every(row => row.every(cell => !cell));
    
    toggleCellBase(row, step);
    
    // Track manual composition creation when first note is added to empty grid
    if (isGridEmpty && !wasActive) {
      // Track manual composition creation (silently - don't block user on failure)
      activityTrackingService.trackManualCompose({
        timestamp: Date.now(),
      }).catch((trackingError) => {
        // Log error but don't show to user - tracking failures should be silent
        console.warn('[Home] Manual composition tracking failed:', trackingError);
      });
      
      // Mark as tracked for this session
      manualComposeTrackedRef.current = true;
    } else if (!isGridEmpty) {
      // Track composition edit for existing compositions (debounced)
      activityTrackingService.trackCompositionEdit({
        action: 'cell_toggle',
        row,
        step,
        wasActive,
        timestamp: Date.now(),
      }).catch((trackingError) => {
        // Log error but don't show to user - tracking failures should be silent
        console.warn('[Home] Composition edit tracking failed:', trackingError);
      });
    }
    
    // Track cell toggle
    analytics.trackGridCellToggle({
      rowIndex: row,
      stepIndex: step,
      activeState: !wasActive,
      instrumentType: instrumentNames[row] || 'unknown',
      isDragOperation: false,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [toggleCellBase, gridState, instrumentNames, analytics]);
  
  const setNote = useCallback((row: number, step: number, note: string) => {
    const oldNote = noteState[row]?.[step];
    setNoteBase(row, step, note);
    
    // Track composition edit (debounced)
    activityTrackingService.trackCompositionEdit({
      action: 'note_change',
      row,
      step,
      oldNote,
      newNote: note,
      timestamp: Date.now(),
    }).catch((trackingError) => {
      console.warn('[Home] Composition edit tracking failed:', trackingError);
    });
    
    // Track note pitch change
    if (oldNote && oldNote !== note) {
      analytics.trackNotePitchChange({
        rowIndex: row,
        stepIndex: step,
        previousNote: oldNote,
        newNote: note,
        instrumentType: instrumentNames[row] || 'unknown',
        timestamp: Date.now(),
        sessionId: '',
      });
    }
  }, [setNoteBase, noteState, instrumentNames, analytics]);
  
  const setInstrument = useCallback((row: number, instrument: import('../types/composition').InstrumentType) => {
    const oldInstrument = instrumentNames[row] || 'unknown';
    setInstrumentBase(row, instrument);
    
    // Track composition edit (debounced)
    activityTrackingService.trackCompositionEdit({
      action: 'instrument_change',
      row,
      oldInstrument,
      newInstrument: instrument,
      timestamp: Date.now(),
    }).catch((trackingError) => {
      console.warn('[Home] Composition edit tracking failed:', trackingError);
    });
    
    // Track instrument change
    analytics.trackInstrumentChange({
      previousInstrumentType: oldInstrument,
      newInstrumentType: instrument,
      rowIndex: row,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [setInstrumentBase, instrumentNames, analytics]);
  
  const toggleParametersEnabled = useCallback((row: number) => {
    const wasEnabled = parametersEnabled[row];
    toggleParametersEnabledBase(row);
    
    // Track custom parameters toggle
    analytics.trackCustomParametersToggle({
      instrumentType: instrumentNames[row] || 'unknown',
      enabled: !wasEnabled,
      rowIndex: row,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [toggleParametersEnabledBase, parametersEnabled, instrumentNames, analytics]);
  
  const addRow = useCallback(() => {
    addRowBase();
    
    // Track row action
    analytics.trackRowAction({
      action: 'add',
      rowIndex: gridState.length,
      totalRowCount: gridState.length + 1,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [addRowBase, gridState.length, analytics]);
  
  const removeRow = useCallback((row: number) => {
    removeRowBase(row);
    
    // Track row action
    analytics.trackRowAction({
      action: 'remove',
      rowIndex: row,
      totalRowCount: gridState.length - 1,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [removeRowBase, gridState.length, analytics]);
  
  const setSteps = useCallback((newSteps: number) => {
    setStepsBase(newSteps);
  }, [setStepsBase]);
  
  const setBpm = useCallback((newBpm: number) => {
    const oldBpm = bpm;
    setBpmBase(newBpm);
    
    // Track BPM change (debounced by analytics service)
    analytics.trackBPMChange({
      previousBPM: oldBpm,
      newBPM: newBpm,
      adjustmentMethod: 'slider',
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [setBpmBase, bpm, analytics]);
  
  // Get instrument parameters for all rows
  const instrumentParameters = instrumentNames.map((_, row) => getInstrumentParameters(row));

  // Get Tone.js controls
  const {
    isReady,
    play,
    pause,
    stop,
    setLoop,
    setVolume: setToneVolume,
    setMute: setToneMute,
    updateVoiceParameters: updateToneVoiceParameters,
    exportAudio,
  } = useTone({
    instrumentTypes: instrumentNames,
    gridState,
    noteState,
    steps,
    bpm,
    instrumentParameters,
    parametersEnabled,
    onStepChange: setCurrentStep,
  });

  // Playback state is managed in component state (see useState above)

  // Check if grid has any active cells
  const hasNotes = gridState.some(row => row.some(cell => cell));

  /**
   * Handle volume change - updates both state and audio engine
   */
  const handleVolumeChange = useCallback((row: number, volume: number) => {
    const oldVolume = volumeState[row] || 0;
    setVolume(row, volume);
    setToneVolume(row, volume);
    
    // Track composition edit (debounced)
    activityTrackingService.trackCompositionEdit({
      action: 'volume_change',
      row,
      oldVolume,
      newVolume: volume,
      timestamp: Date.now(),
    }).catch((trackingError) => {
      console.warn('[Home] Composition edit tracking failed:', trackingError);
    });
    
    // Track volume adjustment (debounced by analytics service)
    analytics.trackVolumeAdjustment({
      rowIndex: row,
      instrumentType: instrumentNames[row] || 'unknown',
      previousVolume: oldVolume,
      newVolume: volume,
      adjustmentMethod: 'slider',
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [setVolume, setToneVolume, volumeState, instrumentNames, analytics]);

  /**
   * Handle mute toggle - updates both state and audio engine
   */
  const handleMuteToggle = useCallback((row: number) => {
    toggleMute(row);
    // Get the new muted state (it will be toggled)
    const newMutedState = !muted[row];
    setToneMute(row, newMutedState);
    
    // Track mute toggle
    analytics.trackMuteToggle({
      rowIndex: row,
      instrumentType: instrumentNames[row] || 'unknown',
      muteState: newMutedState,
      isPlaybackActive: isPlaying,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [toggleMute, muted, setToneMute, instrumentNames, isPlaying, analytics]);

  /**
   * Handle parameter changes - updates both state and audio engine in real-time
   */
  const handleParametersChange = useCallback((row: number, parameters: import('../types/instruments').InstrumentParameters) => {
    updateInstrumentParameters(row, parameters);
    // Update the voice parameters in real-time if audio is ready
    if (isReady) {
      updateToneVoiceParameters(row, parameters);
    }
    
    // Track composition edit (debounced)
    activityTrackingService.trackCompositionEdit({
      action: 'parameters_change',
      row,
      parameters,
      timestamp: Date.now(),
    }).catch((trackingError) => {
      console.warn('[Home] Composition edit tracking failed:', trackingError);
    });
  }, [updateInstrumentParameters, updateToneVoiceParameters, isReady]);

  /**
   * Sync volume and mute states to audio engine when ready
   */
  useEffect(() => {
    if (isReady) {
      // Apply all volume and mute states to the audio engine
      volumeState.forEach((volume, row) => {
        setToneVolume(row, volume);
      });
      muted.forEach((isMuted, row) => {
        setToneMute(row, isMuted);
      });
    }
  }, [isReady, volumeState, muted, setToneVolume, setToneMute]);

  // Get auth state
  const { isAuthenticated } = useAuth();
  
  // Get credits state
  const { hasCredits, consumeCredit, credits } = useCredits();
  
  // In dev mode, treat user as authenticated for easier testing
  const isAuthenticatedOrDevMode = isAuthenticated || DEV_MODE;
  
  // Get navigation
  const navigate = useNavigate();

  // Get deep link parameters
  const deepLinkParams = useDeepLink();

  // Get responsive layout state
  const { isMobile } = useResponsiveLayout();

  // Get mobile state management (only used when isMobile is true)
  const {
    selectedTrackIndex,
    setSelectedTrackIndex,
    activeTab,
    setActiveTab,
    currentStepPage,
    setCurrentStepPage,
    panelStates,
    openPanel,
    closePanel,
  } = useMobileState(gridState.length);

  /**
   * Handle play button
   */
  const handlePlay = useCallback(async () => {
    console.log('[Home] handlePlay called, isReady:', isReady);
    await play();
    setIsPlaying(true);
    setIsPaused(false);
    
    // Track playback start
    const activeCells = gridState.reduce((sum, row) => sum + row.filter(cell => cell).length, 0);
    analytics.trackPlaybackStart({
      compositionLength: steps,
      activeRowCount: gridState.filter(row => row.some(cell => cell)).length,
      activeCellCount: activeCells,
      bpm,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [isReady, play, analytics, bpm, steps, gridState]);

  /**
   * Handle pause button
   */
  const handlePause = useCallback(() => {
    pause();
    setIsPaused(true);
  }, [pause]);

  /**
   * Handle stop button
   */
  const handleStop = useCallback(() => {
    stop();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentStep(0); // Reset to first step
    
    // Track playback stop (simplified - would need playback start time for duration)
    analytics.trackPlaybackStop({
      playbackDuration: 0,
      completedFullLoop: false,
      stopPosition: currentStep,
      timestamp: Date.now(),
      sessionId: '',
    });
  }, [stop, setCurrentStep, analytics]);

  /**
   * Handle loop toggle
   */
  const handleToggleLoop = useCallback(() => {
    const newLoopState = !loopEnabled;
    setLoopEnabled(newLoopState);
    setLoop(newLoopState);
  }, [loopEnabled, setLoop]);

  /**
   * Handle clear grid
   */
  const handleClear = useCallback(() => {
    if (confirm('Clear all notes?')) {
      clearGrid();
      // Reset manual composition tracking flag when grid is cleared
      manualComposeTrackedRef.current = false;
    }
  }, [clearGrid]);

  /**
   * Handle reset to fresh slate
   */
  const handleReset = useCallback(() => {
    if (confirm('Reset to fresh slate? This will clear everything.')) {
      resetGrid();
      // Reset manual composition tracking flag when grid is reset
      manualComposeTrackedRef.current = false;
      // Close the menu panel on mobile
      if (isMobile) {
        closePanel('menu');
        setActiveTab('grid'); // Return to Grid tab
      }
    }
  }, [resetGrid, isMobile, closePanel, setActiveTab]);

  /**
   * Handle insufficient credits
   */
  const handleInsufficientCredits = useCallback((required: number, available: number) => {
    setInsufficientCreditsInfo({ required, available });
    setShowInsufficientCreditsModal(true);
  }, []);

  /**
   * Handle audio export
   */
  const handleExport = useCallback(async () => {
    try {
      console.log('[Home] Starting audio export...');
      
      // Check if user has sufficient credits
      if (!hasCredits(1)) {
        console.log('[Home] Insufficient credits for export');
        handleInsufficientCredits(1, credits);
        return;
      }
      
      // Consume credit for export
      let creditConsumed = false;
      try {
        await consumeCredit('export', 1);
        creditConsumed = true;
        console.log('[Home] Credit consumed for export');
      } catch (error) {
        // Log error but continue with export (graceful degradation)
        console.warn('[Home] Credit consumption failed, proceeding with export:', error);
        setShareStatus('⚠️ Export proceeding, but credit tracking failed. Contact support if this persists.');
        setTimeout(() => setShareStatus(''), 7000);
      }
      
      // Export audio as WAV blob
      const blob = await exportAudio();
      
      // Get composition for filename
      const composition = getComposition();
      const filename = composition.title 
        ? `${composition.title}.wav`
        : `8bit-composition-${Date.now()}.wav`;
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('[Home] Audio export completed successfully');
      
      // Track export with credit consumption info
      const activeCells = gridState.reduce((sum, row) => sum + row.filter(cell => cell).length, 0);
      analytics.trackCompositionExport({
        exportFormat: 'wav',
        compositionDuration: 0,
        instrumentCount: gridState.length,
        activeCellCount: activeCells,
        creditConsumed,
        timestamp: Date.now(),
        sessionId: '',
      });
    } catch (error) {
      console.error('[Home] Export failed', error);
      alert('Failed to export audio. Please try again.');
    }
  }, [exportAudio, getComposition, analytics, hasCredits, consumeCredit, credits, handleInsufficientCredits, gridState]);

  /**
   * Handle share composition
   */
  const handleShare = useCallback(async () => {
    try {
      // On mobile, show modal with loading state
      if (isMobile) {
        setShowShareLinkModal(true);
        setIsGeneratingShareLink(true);
        setShareLinkError('');
        setShareUrl('');
      } else {
        setShareStatus('⏳ Generating share link...');
      }
      
      const composition = getComposition();
      const shareResponse = await shareAPI.share(composition);
      
      // On mobile, update modal with the URL
      if (isMobile) {
        setShareUrl(shareResponse.url);
        setIsGeneratingShareLink(false);
      } else {
        // On desktop, copy to clipboard automatically
        await navigator.clipboard.writeText(shareResponse.url);
        setShareStatus(`✅ Link copied to clipboard: ${shareResponse.url}`);
        setTimeout(() => setShareStatus(''), 5000);
      }
      
      // Track composition share event (silently - don't block user on failure)
      activityTrackingService.trackCompositionShare({
        shareId: shareResponse.id,
        shareUrl: shareResponse.url,
        compositionTitle: composition.title || 'Untitled',
        instrumentCount: composition.channels.length,
        patternLength: composition.channels[0]?.patternLength || steps,
        tempo: composition.tempo,
        timestamp: Date.now(),
      }).catch((trackingError) => {
        // Log error but don't show to user - tracking failures should be silent
        console.warn('[Home] Composition share tracking failed:', trackingError);
      });
    } catch (error) {
      console.error('[Home] Share failed', error);
      
      if (isMobile) {
        setShareLinkError('Failed to generate share link. Please try again.');
        setIsGeneratingShareLink(false);
      } else {
        setShareStatus('❌ Failed to share composition. Please try again.');
        setTimeout(() => setShareStatus(''), 5000);
      }
    }
  }, [getComposition, isMobile, steps]);

  /**
   * Handle save composition button click
   */
  const handleSaveClick = useCallback(() => {
    // Check authentication (bypass in dev mode for testing)
    if (!isAuthenticatedOrDevMode) {
      setShareStatus('❌ You must be logged in to save compositions');
      setTimeout(() => setShareStatus(''), 5000);
      return;
    }

    // In dev mode, show a warning that this is mock mode
    if (DEV_MODE && !isAuthenticated) {
      setShareStatus('⚠️ DEV MODE: Simulating save (no actual API call)');
      setTimeout(() => setShareStatus(''), 3000);
    }

    // Open save dialog
    setShowSaveDialog(true);
  }, [isAuthenticatedOrDevMode, isAuthenticated]);

  /**
   * Handle save composition
   */
  const handleSaveComposition = useCallback(async (title: string, saveAsNew: boolean) => {
    setIsSaving(true);
    
    try {
      const composition = getComposition();
      
      // DEV MODE: Simulate save without API call
      if (DEV_MODE && !isAuthenticated) {
        // Simulate delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Simulate limit error if flag is set
        if (DEV_MOCK_LIMIT_ERROR) {
          throw new APIServiceError(
            'Composition limit reached (15/15). Upgrade to premium for more storage.',
            403,
            'COMPOSITION_LIMIT_REACHED'
          );
        }
        
        // Simulate successful save with mock ID
        const mockId = `dev-${Date.now()}`;
        setCurrentCompositionId(mockId);
        setCompositionTitle(title);
        setShareStatus(`✅ [DEV MODE] Composition "${title}" saved (simulated)`);
        setShowSaveDialog(false);
        setTimeout(() => setShareStatus(''), 5000);
        setIsSaving(false);
        return;
      }
      
      // Determine if we're updating or creating new
      const isUpdate = currentCompositionId && !saveAsNew;
      
      if (isUpdate) {
        // Update existing composition
        await compositionAPI.update(currentCompositionId, { 
          title,
          composition 
        });
        
        setCompositionTitle(title);
        setShareStatus(`${OPERATION_ERROR_MESSAGES.save.success} "${title}" updated! View in My Compositions`);
        
        // Track update
        analytics.trackCompositionSaveLoad({
          action: 'save',
          storageSource: 'cloud',
          compositionMetadata: {
            title,
            instrumentCount: composition.channels.length,
          },
          timestamp: Date.now(),
          sessionId: '',
        });
      } else {
        // Save new composition
        const metadata = await compositionAPI.save(composition, title);
        
        setCurrentCompositionId(metadata.compositionId);
        setCompositionTitle(title);
        const successMessage = `${OPERATION_ERROR_MESSAGES.save.success} "${title}" saved! View in My Compositions`;
        console.log('[Home] Setting success message:', successMessage);
        setShareStatus(successMessage);
        
        // Track save
        analytics.trackCompositionSaveLoad({
          action: 'save',
          storageSource: 'cloud',
          compositionMetadata: {
            title,
            instrumentCount: composition.channels.length,
          },
          timestamp: Date.now(),
          sessionId: '',
        });
      }
      
      // Close dialog
      setShowSaveDialog(false);
      
      // Clear status after 5 seconds
      setTimeout(() => setShareStatus(''), 5000);
    } catch (error) {
      console.error('[Home] Save failed', error);
      
      // Check if error is composition limit reached
      if (error instanceof APIServiceError && error.code === 'COMPOSITION_LIMIT_REACHED') {
        // Close save dialog
        setShowSaveDialog(false);
        
        // Get user profile to show current count and tier
        const profile = sessionManager.getProfile();
        if (profile) {
          setCompositionCount(profile.compositions?.length || 0);
          setShowLimitModal(true);
        } else {
          // Fallback if profile not available (dev mode)
          if (DEV_MODE) {
            setCompositionCount(15);
            setShowLimitModal(true);
          } else {
            const errorMessage = formatErrorMessage(error, true);
            setShareStatus(errorMessage);
            setTimeout(() => setShareStatus(''), 5000);
          }
        }
      } else {
        // Show error message for other errors
        const errorMessage = formatErrorMessage(error, true);
        setShareStatus(errorMessage);
        setTimeout(() => setShareStatus(''), 5000);
      }
    } finally {
      setIsSaving(false);
    }
  }, [getComposition, currentCompositionId, analytics, isAuthenticated]);

  /**
   * Handle close save dialog
   */
  const handleCloseSaveDialog = useCallback(() => {
    if (!isSaving) {
      setShowSaveDialog(false);
    }
  }, [isSaving]);

  /**
   * Handle close limit modal
   */
  const handleCloseLimitModal = useCallback(() => {
    setShowLimitModal(false);
  }, []);

  /**
   * Handle navigate to library from limit modal
   */
  const handleNavigateToLibrary = useCallback(() => {
    navigate('/my-compositions');
  }, [navigate]);

  /**
   * Handle composition generated from AI
   */
  const handleCompositionGenerated = useCallback(
    (composition: Composition) => {
      loadComposition(composition);
      // Reset manual composition tracking flag when AI generates a composition
      manualComposeTrackedRef.current = false;
    },
    [loadComposition]
  );

  /**
   * Handle close insufficient credits modal
   */
  const handleCloseInsufficientCreditsModal = useCallback(() => {
    setShowInsufficientCreditsModal(false);
  }, []);

  /**
   * Handle help button
   */
  const handleHelp = useCallback(() => {
    setIsHelpOpen(true);
  }, []);

  /**
   * Handle close help modal
   */
  const handleCloseHelp = useCallback(() => {
    setIsHelpOpen(false);
  }, []);

  /**
   * Handle open template browser
   */
  const handleOpenTemplateBrowser = useCallback(() => {
    setIsTemplateBrowserOpen(true);
  }, []);

  /**
   * Handle close template browser
   */
  const handleCloseTemplateBrowser = useCallback(() => {
    setIsTemplateBrowserOpen(false);
  }, []);

  /**
   * Handle template selection
   */
  const handleSelectTemplate = useCallback((template: Template) => {
    // Check for unsaved changes
    if (hasUnsavedChanges) {
      // Store template and show confirmation dialog
      setPendingTemplate(template);
      setShowConfirmDialog(true);
    } else {
      // Load template directly
      loadTemplateComposition(template);
    }
  }, [hasUnsavedChanges]);

  /**
   * Load template composition
   */
  const loadTemplateComposition = useCallback((template: Template) => {
    try {
      // Validate template composition before loading
      if (!template.composition) {
        throw new Error('Template composition data is missing');
      }

      // Load the template composition
      loadComposition(template.composition);
      
      // Update initial composition reference
      initialCompositionRef.current = JSON.stringify(template.composition);
      setHasUnsavedChanges(false);
      
      // Reset manual composition tracking flag when template is loaded
      manualComposeTrackedRef.current = false;
      
      // Close template browser
      setIsTemplateBrowserOpen(false);
      
      // Show success notification
      setShareStatus(`✅ Template "${template.name}" loaded!`);
      setTimeout(() => setShareStatus(''), 3000);
      
      console.log('[Home] Template loaded:', template.name);
      
      // Track template load
      const category = Array.isArray(template.category) ? template.category[0] : template.category;
      analytics.trackTemplateLoad({
        templateId: template.id,
        templateCategory: category || 'unknown',
        isUserCreated: false,
        isSystemProvided: true,
        timestamp: Date.now(),
        sessionId: '',
      });
    } catch (error) {
      console.error('[Home] Failed to load template:', {
        template: template.name,
        error
      });
      
      // Keep template browser open so user can try another template
      // Don't close: setIsTemplateBrowserOpen(false);
      
      // Show detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setShareStatus(`❌ Failed to load template: ${errorMessage}. Please try a different template.`);
      setTimeout(() => setShareStatus(''), 7000);
    }
  }, [loadComposition, analytics]);

  /**
   * Handle confirm load template (with unsaved changes)
   */
  const handleConfirmLoadTemplate = useCallback(() => {
    if (pendingTemplate) {
      loadTemplateComposition(pendingTemplate);
      setPendingTemplate(null);
    }
    setShowConfirmDialog(false);
  }, [pendingTemplate, loadTemplateComposition]);

  /**
   * Handle cancel load template
   */
  const handleCancelLoadTemplate = useCallback(() => {
    setPendingTemplate(null);
    setShowConfirmDialog(false);
  }, []);

  /**
   * Mobile-specific handlers
   */

  /**
   * Handle track selection in mobile mode
   * When a track is selected from the Tracks panel, switch to Grid tab
   * 
   * State Preservation (Requirement 13.1):
   * - selectedTrackIndex is updated and persists across all tab switches
   * - Grid state, playback position, and page position remain unchanged
   */
  const handleTrackSelect = useCallback((trackIndex: number) => {
    setSelectedTrackIndex(trackIndex);
    setActiveTab('grid'); // Switch to Grid tab to show the selected track
    closePanel('tracks');
  }, [setSelectedTrackIndex, setActiveTab, closePanel]);

  /**
   * Handle BPM tap in mobile mode
   */
  const handleBPMTap = useCallback(() => {
    openPanel('bpm');
  }, [openPanel]);

  /**
   * Handle Steps tap in mobile mode
   */
  const handleStepsTap = useCallback(() => {
    openPanel('steps');
  }, [openPanel]);

  /**
   * Handle menu tap in mobile mode
   */
  const handleMenuTap = useCallback(() => {
    openPanel('menu');
  }, [openPanel]);

  /**
   * Handle track info tap in mobile mode
   */
  const handleTrackInfoTap = useCallback(() => {
    openPanel('tracks');
  }, [openPanel]);

  /**
   * Handle volume tap in mobile mode
   */
  const handleVolumeTap = useCallback(() => {
    openPanel('volume');
  }, [openPanel]);

  /**
   * Handle edit tap in mobile mode
   */
  const handleEditTap = useCallback(() => {
    openPanel('sound');
  }, [openPanel]);

  /**
   * Handle more actions tap in mobile mode
   * Opens the track actions menu
   */
  const handleMoreActionsTap = useCallback(() => {
    openPanel('trackActions');
  }, [openPanel]);

  /**
   * Handle duplicate track action
   */
  const handleDuplicateTrack = useCallback(() => {
    addRow();
    // Copy grid state
    const newRowIndex = gridState.length;
    gridState[selectedTrackIndex]?.forEach((cell, step) => {
      if (cell) {
        toggleCell(newRowIndex, step);
      }
    });
    // Copy note state
    noteState[selectedTrackIndex]?.forEach((note, step) => {
      if (note) {
        setNote(newRowIndex, step, note);
      }
    });
    // Copy instrument
    setInstrument(newRowIndex, instrumentNames[selectedTrackIndex] || 'square');
    // Copy volume
    handleVolumeChange(newRowIndex, volumeState[selectedTrackIndex] || 0);
    setShareStatus(`✅ Track ${selectedTrackIndex + 1} duplicated`);
    setTimeout(() => setShareStatus(''), 3000);
  }, [selectedTrackIndex, gridState, noteState, instrumentNames, volumeState, addRow, toggleCell, setNote, setInstrument, handleVolumeChange, setShareStatus]);

  /**
   * Handle clear track pattern action
   */
  const handleClearTrackPattern = useCallback(() => {
    gridState[selectedTrackIndex]?.forEach((_, step) => {
      if (gridState[selectedTrackIndex]?.[step]) {
        toggleCell(selectedTrackIndex, step);
      }
    });
    setShareStatus(`✅ Track ${selectedTrackIndex + 1} pattern cleared`);
    setTimeout(() => setShareStatus(''), 3000);
  }, [selectedTrackIndex, gridState, toggleCell, setShareStatus]);

  /**
   * Handle delete track action
   */
  const handleDeleteTrack = useCallback(() => {
    if (gridState.length > 1) {
      const trackToDelete = selectedTrackIndex;
      const newLength = gridState.length - 1;
      
      removeRow(trackToDelete);
      
      // Adjust selected track if needed
      if (trackToDelete >= newLength) {
        setSelectedTrackIndex(Math.max(0, newLength - 1));
      }
      
      setShareStatus(`✅ Track ${trackToDelete + 1} deleted`);
      setTimeout(() => setShareStatus(''), 3000);
    }
  }, [selectedTrackIndex, gridState, removeRow, setSelectedTrackIndex, setShareStatus]);

  /**
   * Handle tab change in mobile mode
   * Requirements: 6.3, 6.4, 6.5, 6.6, 6.8
   * 
   * State Preservation (Requirements 13.1, 13.2, 13.5, 10.7):
   * - selectedTrackIndex persists across tab switches
   * - Grid state persists (managed by useGrid hook)
   * - Playback position (currentStep) persists (managed by useGrid hook)
   * - Page position (currentStepPage) persists (managed by useMobileState hook)
   */
  const handleTabChange = useCallback((tab: import('../components/mobile').MobileTab) => {
    setActiveTab(tab);
    
    // Close all panels first
    closePanel('tracks');
    closePanel('sound');
    closePanel('volume');
    closePanel('bpm');
    closePanel('steps');
    closePanel('menu');
    
    // Open corresponding panel based on tab
    // Grid tab: Show TrackHeader + StepGrid (default view, no panel)
    // Tracks tab: Open TracksPanel
    // Sound tab: Open SoundPanel
    // Song tab: Open song settings panel
    if (tab === 'tracks') {
      openPanel('tracks');
    } else if (tab === 'sound') {
      openPanel('sound');
    } else if (tab === 'song') {
      openPanel('menu'); // Using 'menu' panel state for song settings
    }
    // Grid tab doesn't open any panel - just shows the grid
  }, [setActiveTab, openPanel, closePanel]);

  /**
   * Handle BPM change from picker
   */
  const handleBPMChange = useCallback((newBpm: number) => {
    setBpm(newBpm);
    closePanel('bpm');
  }, [setBpm, closePanel]);

  /**
   * Handle Steps change from picker
   */
  const handleStepsChange = useCallback((newSteps: number) => {
    setSteps(newSteps);
    closePanel('steps');
  }, [setSteps, closePanel]);

  /**
   * AI Generation handlers
   */
  
  // Retro game-style loading messages
  const loadingMessages = [
    '🎮 LOADING SOUND CHIP',
    '🎵 COMPOSING MELODY',
    '🎹 ARRANGING CHANNELS',
    '🎸 TUNING INSTRUMENTS',
    '🥁 PROGRAMMING DRUMS',
    '🎼 MIXING TRACKS',
    '✨ ADDING 8-BIT MAGIC',
    '🔊 ADJUSTING VOLUMES',
    '🎶 CRAFTING HARMONIES',
    '⚡ POWERING UP SYNTHS',
    '🌟 FINALIZING COMPOSITION',
    '🎺 CALIBRATING FREQUENCIES',
    '🎻 LAYERING MELODIES',
    '🔮 GENERATING PATTERNS',
    '💫 SYNCING RHYTHMS',
    '🎪 ORCHESTRATING SOUNDS',
    '🌈 PAINTING AUDIO WAVES',
    '🚀 LAUNCHING SEQUENCES',
    '⭐ POLISHING NOTES',
    '🎭 BALANCING DYNAMICS',
    '🔥 IGNITING CREATIVITY',
    '💎 REFINING TONES',
    '🌙 MOONWALKING BASSLINES',
    '☄️ DROPPING THE BEAT',
    '🎯 TARGETING PERFECTION',
  ];

  /**
   * Rotate loading messages and animate progress bar during generation
   */
  useEffect(() => {
    if (!isGenerating) {
      setGenerationProgress(0);
      setGenerationMessage('');
      return;
    }

    let messageIndex = 0;
    let progress = 0;
    
    // Rotate messages every 3 seconds
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      const message = loadingMessages[messageIndex];
      if (message) {
        setGenerationMessage(message);
      }
    }, 3000);

    // Animate progress bar with logarithmic slowdown
    const progressInterval = setInterval(() => {
      const remaining = 90 - progress;
      const increment = Math.max(0.5, remaining * 0.08);
      progress = Math.min(progress + increment, 90);
      setGenerationProgress(progress);
    }, 1000);

    // Set initial message
    const initialMessage = loadingMessages[0];
    if (initialMessage) {
      setGenerationMessage(initialMessage);
    }

    return () => {
      clearInterval(messageInterval);
      clearInterval(progressInterval);
    };
  }, [isGenerating]);

  /**
   * Handle AI generation request
   */
  const handleAIGenerate = useCallback(async (prompt: string) => {
    // Clear any previous errors
    setGenerationError('');
    
    // Check authentication
    if (!isAuthenticated) {
      setGenerationError('Please sign in to use AI generation.');
      return;
    }

    // Check credits
    const requiredCredits = 1;
    if (!hasCredits(requiredCredits)) {
      setGenerationError('Insufficient credits. Please purchase more credits to continue.');
      handleInsufficientCredits(requiredCredits, credits);
      return;
    }

    // Start generation
    setIsGenerating(true);
    setGenerationProgress(0);

    // Determine mode (new or edit)
    const isEditing = hasNotes;

    try {
      // Call AI generation API
      const result = await generateAPI.generate(
        prompt,
        isEditing ? getComposition() : undefined
      );

      // Consume credit after successful generation
      try {
        const remainingCredits = await consumeCredit('generate', requiredCredits);
        
        // Complete progress bar
        setGenerationProgress(100);

        // Apply generated composition
        loadComposition(result);
        
        // Reset manual composition tracking flag
        manualComposeTrackedRef.current = false;

        // Track AI generation activity
        try {
          await activityTrackingService.trackAIGeneration({
            prompt,
            mode: isEditing ? 'edit' : 'new',
            timestamp: Date.now(),
          });
        } catch (trackingError) {
          console.warn('[Home] Activity tracking failed:', trackingError);
        }

        // Show success message
        setShareStatus(`✅ LEVEL COMPLETE! (${remainingCredits} credits remaining)`);
        setTimeout(() => setShareStatus(''), 5000);

        // Close panel after short delay to show completion
        setTimeout(() => {
          closePanel('aiGenerate');
          setGenerationError('');
        }, 1500);
      } catch (creditError) {
        console.error('[Home] Failed to consume credit:', creditError);
        setGenerationError('Failed to deduct credit. Please try again.');
        setShareStatus('❌ Failed to deduct credit. Please try again.');
        setTimeout(() => setShareStatus(''), 5000);
      }
    } catch (error) {
      console.error('[Home] Generation failed', error);
      
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';
      
      setGenerationError(`Generation failed: ${errorMessage}`);
      setShareStatus(`❌ Generation failed: ${errorMessage}`);
      setTimeout(() => setShareStatus(''), 5000);
    } finally {
      setIsGenerating(false);
    }
  }, [
    isAuthenticated,
    hasCredits,
    credits,
    hasNotes,
    getComposition,
    consumeCredit,
    loadComposition,
    handleInsufficientCredits,
    closePanel,
  ]);

  /**
   * Handle open AI generate panel
   */
  const handleOpenAIGenerate = useCallback(() => {
    closePanel('menu');
    setGenerationError(''); // Clear any previous errors
    openPanel('aiGenerate');
  }, [closePanel, openPanel]);

  /**
   * Show restoration notification on initial load if composition exists
   */
  useEffect(() => {
    // Only run once on mount, and only if no shared composition is being loaded
    if (!hasShownRestorationNotification.current && !sharedComposition) {
      // Check if a composition was loaded from localStorage
      if (storageService.hasComposition()) {
        setShareStatus('✅ Composition restored from previous session');
        setTimeout(() => setShareStatus(''), 3000);
        hasShownRestorationNotification.current = true;
      }
    }
  }, []); // Empty dependency array - only run once on mount

  /**
   * Load shared composition when provided
   */
  useEffect(() => {
    if (sharedComposition) {
      loadComposition(sharedComposition);
      // Reset manual composition tracking flag when shared composition is loaded
      manualComposeTrackedRef.current = false;
      // Show a brief notification
      setShareStatus('✅ Shared composition loaded!');
      setTimeout(() => setShareStatus(''), 3000);
    }
  }, [sharedComposition, loadComposition]);

  /**
   * Handle deep link parameters from URL
   * Processes template, remix, BPM, and key parameters
   */
  useEffect(() => {
    // Only process deep links if there are parameters and no shared composition is being loaded
    if (!sharedComposition && Object.keys(deepLinkParams).length > 0) {
      const handleDeepLinkAsync = async () => {
        console.log('[Home] Processing deep link parameters:', deepLinkParams);
        
        try {
          const result = await deepLinkHandler.handleDeepLink(deepLinkParams);
          
          if (!result.success) {
            // Show error but don't block user
            if (result.error) {
              setShareStatus(`⚠️ ${result.error}`);
              setTimeout(() => setShareStatus(''), 5000);
            }
            return;
          }

          // Apply the result based on what was loaded
          if (result.composition) {
            // Load remix composition
            loadComposition(result.composition);
            manualComposeTrackedRef.current = false;
            setShareStatus('✅ Remix track loaded! Start editing to make it your own.');
            setTimeout(() => setShareStatus(''), 5000);
          } else if (result.template) {
            // Load template
            loadTemplateComposition(result.template);
          }

          // Apply BPM if provided (and not already set by composition/template)
          if (result.bpm && !result.composition) {
            setBpm(result.bpm);
          }

          // Note: Key parameter is returned but not currently applied
          // as the composition structure doesn't have a key field yet
          if (result.key) {
            console.log('[Home] Key parameter received but not applied:', result.key);
          }

          // Show success message if we loaded something
          if (result.composition || result.template) {
            console.log('[Home] Deep link processed successfully:', result.entryPoint);
          }
        } catch (error) {
          console.error('[Home] Failed to process deep link:', error);
          setShareStatus('⚠️ Failed to process link parameters');
          setTimeout(() => setShareStatus(''), 5000);
        }
      };

      handleDeepLinkAsync();
    }
  }, [deepLinkParams, sharedComposition, loadComposition, loadTemplateComposition, setBpm]);

  /**
   * Track initial composition and detect changes
   */
  useEffect(() => {
    if (isInitialLoadRef.current) {
      // Store initial composition state
      const composition = getComposition();
      initialCompositionRef.current = JSON.stringify(composition);
      isInitialLoadRef.current = false;
      setHasUnsavedChanges(false);
    } else {
      // Check if composition has changed
      const currentComposition = getComposition();
      const currentState = JSON.stringify(currentComposition);
      const hasChanged = currentState !== initialCompositionRef.current;
      setHasUnsavedChanges(hasChanged);
    }
  }, [gridState, noteState, volumeState, instrumentNames, steps, bpm, getComposition]);

  /**
   * Cleanup save indicator timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (saveIndicatorTimeoutRef.current) {
        clearTimeout(saveIndicatorTimeoutRef.current);
      }
    };
  }, []);

  // FAQ structured data
  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is 8-Bit Composer free to use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! 8-Bit Composer is completely free to use. You can create, edit, export, and share your compositions without any cost. AI generation features are available with an affordable credit system."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to download or install anything?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No downloads or installations required. 8-Bit Composer runs entirely in your web browser on any device. Just visit the website and start composing instantly."
        }
      },
      {
        "@type": "Question",
        "name": "Can I export my music?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! You can export your compositions as high-quality WAV audio files. Simply click the export button and download your creation to use in games, videos, or anywhere else."
        }
      },
      {
        "@type": "Question",
        "name": "What is the AI generation feature?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our AI-powered generation uses Claude AI via AWS Bedrock to create 8-bit music based on your text descriptions. Simply describe the type of music you want (e.g., 'upbeat battle theme') and the AI will generate it for you."
        }
      },
      {
        "@type": "Question",
        "name": "Can I share my compositions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! You can instantly share your compositions with a unique link. The link is automatically copied to your clipboard and can be shared with anyone."
        }
      }
    ]
  };

  // Structured data for home page
  const appData = {
    "@context": "https://schema.org",
    "@type": ["WebApplication", "SoftwareApplication", "CreativeWork"],
    "name": "8-Bit Composer",
    "alternateName": "8BitComposer",
    "description": "Free online chiptune music maker and sequencer for creating retro 8-bit style music",
    "url": "https://www.8bitcomposer.com/",
    "image": "https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png",
    "screenshot": "https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png",
    "applicationCategory": ["MusicApplication", "EntertainmentApplication", "MultimediaApplication"],
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript and Web Audio API support",
    "softwareVersion": "1.0",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2026-12-31"
    },
    "creator": {
      "@type": "Person",
      "name": "Dorian Smith",
      "url": "https://doriansmith.dev",
      "jobTitle": "Full-Stack Developer"
    },
    "author": {
      "@type": "Person",
      "name": "Dorian Smith"
    },
    "publisher": {
      "@type": "Organization",
      "name": "8-Bit Composer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://s3.us-west-2.amazonaws.com/www.8bitcomposer.com/logo.png"
      }
    },
    "featureList": [
      "AI-powered melody generation using Claude AI",
      "Drag and drop step sequencer interface",
      "Multiple authentic 8-bit instruments (square wave, triangle, sawtooth, noise)",
      "WAV audio export functionality",
      "Real-time playback with adjustable BPM",
      "Cloud saving and instant sharing",
      "No installation required - runs in browser",
      "Free to use with optional credits for AI generation and export"
    ],
    "keywords": "8-bit music maker, chiptune creator, online music sequencer, retro music composer, pixel music, video game music, NES music, gameboy music, free music software, browser music maker, step sequencer, music production tool",
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "datePublished": "2024-01-01",
    "dateModified": "2025-01-15"
  };

  // Combine all structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [appData, faqData]
  };

  return (
    <>
      <SEO
        title="8-Bit Composer – Free Online Chiptune Music Maker & Sequencer"
        description="Create nostalgic 8-bit chiptune music online with our free browser-based sequencer. Drag-and-drop grid interface, export to WAV, and share your retro compositions instantly. No downloads required!"
        structuredData={structuredData}
      />
      
      {/* Purchase Status Handler */}
      <PurchaseStatusHandler />
      
      {/* Dev Mode Banner */}
      {DEV_MODE && !isAuthenticated && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.2) 0%, rgba(255, 140, 0, 0.15) 100%)',
          border: '2px solid rgba(255, 165, 0, 0.5)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          margin: '1rem auto',
          maxWidth: '800px',
          textAlign: 'center',
          fontSize: '0.85rem',
          color: 'var(--color-accent)',
          boxShadow: '0 2px 8px rgba(255, 165, 0, 0.2)',
        }}>
          <strong>🔧 DEV MODE</strong> - You're running in development mode with simulated authentication. 
          Saves won't persist to the database.
        </div>
      )}
      
      <main id="main-content">
        {isMobile ? (
          // Mobile Layout
          <MobileComposerLayout>
            <TopTransportBar
              isPlaying={isPlaying}
              isPaused={isPaused}
              loopEnabled={loopEnabled}
              hasNotes={hasNotes}
              bpm={bpm}
              steps={steps}
              onPlay={handlePlay}
              onPause={handlePause}
              onStop={handleStop}
              onToggleLoop={handleToggleLoop}
              onBPMTap={handleBPMTap}
              onStepsTap={handleStepsTap}
              onMenuTap={handleMenuTap}
            />

            {/* Grid Viewport Wrapper
                Requirements: 2.4, 4.5, 12.4
                
                This wrapper ensures the grid remains visible at all times by:
                1. flex: 1 - Fills available vertical space between top and bottom sections
                2. minHeight: 0 - Prevents flex overflow, allowing internal scrolling if needed
                3. display: flex, flexDirection: column - Establishes flex context for TrackHeader and StepGrid
                
                The minHeight: 0 is critical to prevent the grid from pushing content off-screen.
                Without it, flex items can overflow their container when content exceeds available space.
            */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              minHeight: 0,
              width: '100%',
              boxSizing: 'border-box',
              overflow: 'hidden'
            }}>
              <TrackHeader
                trackIndex={selectedTrackIndex}
                trackName={`Track ${selectedTrackIndex + 1}`}
                instrumentName={instrumentNames[selectedTrackIndex] || 'square'}
                isMuted={muted[selectedTrackIndex] || false}
                onTrackInfoTap={handleTrackInfoTap}
                onMuteToggle={() => handleMuteToggle(selectedTrackIndex)}
                onVolumeTap={handleVolumeTap}
                onEditTap={handleEditTap}
                onMoreActionsTap={handleMoreActionsTap}
              />

              <StepGrid
                trackIndex={selectedTrackIndex}
                gridState={gridState[selectedTrackIndex] || []}
                noteState={noteState[selectedTrackIndex] || []}
                steps={steps}
                currentStep={currentStep}
                isPlaying={isPlaying}
                currentPage={currentStepPage}
                onCellToggle={(step) => toggleCell(selectedTrackIndex, step)}
                onNoteChange={(step, note) => setNote(selectedTrackIndex, step, note)}
                onPageChange={setCurrentStepPage}
              />
            </div>

            <BottomTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />

            {/* Mobile Panels */}
            {panelStates.tracks && (
              <TracksPanel
                tracks={gridState.map((_, index) => ({
                  index,
                  name: `Track ${index + 1}`,
                  instrument: instrumentNames[index] || 'square',
                  muted: muted[index] || false,
                  solo: false, // TODO: Implement solo functionality
                  hasNotes: gridState[index]?.some(cell => cell) || false,
                }))}
                selectedTrackIndex={selectedTrackIndex}
                onTrackSelect={handleTrackSelect}
                onMuteToggle={handleMuteToggle}
                onSoloToggle={(index) => {
                  // TODO: Implement solo functionality
                  console.log('[Home] Solo toggle for track', index);
                }}
                onAddTrack={addRow}
                onClose={() => {
                  closePanel('tracks');
                  setActiveTab('grid'); // Return to Grid tab when panel is closed
                }}
              />
            )}

            {panelStates.sound && (
              <SoundPanel
                trackIndex={selectedTrackIndex}
                instrument={instrumentNames[selectedTrackIndex] || 'square'}
                parameters={instrumentParameters[selectedTrackIndex] || {}}
                parametersEnabled={parametersEnabled[selectedTrackIndex] || false}
                effectsChain={effectsChains[selectedTrackIndex] || []}
                onInstrumentChange={(inst) => setInstrument(selectedTrackIndex, inst)}
                onParametersChange={(params) => handleParametersChange(selectedTrackIndex, params)}
                onParametersEnabledChange={(enabled) => {
                  if (enabled !== parametersEnabled[selectedTrackIndex]) {
                    toggleParametersEnabled(selectedTrackIndex);
                  }
                }}
                onEffectsChange={(effects) => setEffectsChain(selectedTrackIndex, effects)}
                onClose={() => {
                  closePanel('sound');
                  setActiveTab('grid'); // Return to Grid tab when panel is closed
                }}
              />
            )}

            {panelStates.volume && (
              <VolumeBottomSheet
                trackIndex={selectedTrackIndex}
                trackName={`Track ${selectedTrackIndex + 1}`}
                volume={volumeState[selectedTrackIndex] || 0}
                onVolumeChange={(volume) => handleVolumeChange(selectedTrackIndex, volume)}
                onClose={() => closePanel('volume')}
              />
            )}

            {panelStates.bpm && (
              <BPMPickerModal
                currentBPM={bpm}
                onBPMChange={handleBPMChange}
                onClose={() => closePanel('bpm')}
              />
            )}

            {panelStates.steps && (
              <StepsPickerModal
                currentSteps={steps}
                onStepsChange={handleStepsChange}
                onClose={() => closePanel('steps')}
              />
            )}

            {panelStates.menu && (
              <SongSettingsPanel
                bpm={bpm}
                steps={steps}
                onBPMTap={handleBPMTap}
                onStepsTap={handleStepsTap}
                onAIGenerate={handleOpenAIGenerate}
                onTemplates={() => {
                  closePanel('menu');
                  setIsTemplateBrowserOpen(true);
                }}
                onSave={handleSaveClick}
                onShare={handleShare}
                onExport={handleExport}
                onReset={handleReset}
                onClose={() => {
                  closePanel('menu');
                  setActiveTab('grid'); // Return to Grid tab when panel is closed
                }}
              />
            )}

            {panelStates.trackActions && (
              <TrackActionsMenu
                trackName={`Track ${selectedTrackIndex + 1}`}
                isLastTrack={gridState.length === 1}
                onDuplicate={handleDuplicateTrack}
                onClear={handleClearTrackPattern}
                onDelete={handleDeleteTrack}
                onClose={() => closePanel('trackActions')}
              />
            )}

            {panelStates.aiGenerate && (
              <AIGeneratePanel
                onGenerate={handleAIGenerate}
                isGenerating={isGenerating}
                loadingMessage={generationMessage}
                progress={generationProgress}
                errorMessage={generationError}
                onClose={() => {
                  closePanel('aiGenerate');
                  setGenerationError(''); // Clear error when closing
                }}
              />
            )}

            {/* Share Link Modal */}
            <ShareLinkModal
              isOpen={showShareLinkModal}
              shareUrl={shareUrl}
              isLoading={isGeneratingShareLink}
              error={shareLinkError}
              onClose={() => {
                setShowShareLinkModal(false);
                setShareUrl('');
                setShareLinkError('');
              }}
            />
            {/* Footer Button - Always visible on mobile */}
            <FooterButton onClick={() => setIsFooterDrawerOpen(true)} />

            {/* Footer Drawer */}
            <FooterDrawer
              isOpen={isFooterDrawerOpen}
              onClose={() => setIsFooterDrawerOpen(false)}
            />
          </MobileComposerLayout>
        ) : (
          // Desktop Layout
          <>
        {/* Hero Section */}
      <section className="text-center mb-8 px-4" style={{ marginTop: '2rem' }}>
        <h1 className="text-2xl md:text-3xl mb-4 text-accent">
          🎵 Compose Retro Chiptunes in Your Browser
        </h1>
        <p className="text-xs md:text-sm mb-6 max-w-2xl mx-auto">
          Create nostalgic 8-bit music with our grid-based sequencer.
          Perfect for game developers, musicians, and retro enthusiasts. No
          downloads required – start composing instantly!
        </p>
      </section>

      {/* Status Notification - Prominent at top */}
      {shareStatus && (
        <section id="share-section" aria-label="Status notification" style={{ marginBottom: '1.5rem' }}>
          <div
            id="share-status"
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              padding: '1rem 1.5rem',
              backgroundColor: 'var(--color-surface)',
              border: '2px solid var(--color-accent)',
              borderRadius: '0.5rem',
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-fg)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              animation: 'notification-slide-in 0.3s ease-out'
            }}
            aria-live="polite"
            role="status"
          >
            {shareStatus}
          </div>
        </section>
      )}

      {/* Playback Controls */}
      <section id="playback-controls" aria-label="Playback controls">
        <PlaybackControls
          isPlaying={isPlaying}
          isPaused={isPaused}
          loopEnabled={loopEnabled}
          hasNotes={hasNotes}
          steps={steps}
          bpm={bpm}
          onPlay={handlePlay}
          onPause={handlePause}
          onStop={handleStop}
          onToggleLoop={handleToggleLoop}
          onClear={handleClear}
          onReset={handleReset}
          onExport={handleExport}
          onShare={handleShare}
          onStepsChange={setSteps}
          onBpmChange={setBpm}
          onHelp={handleHelp}
          onSave={handleSaveClick}
          onLoad={() => navigate('/my-compositions')}
          isAuthenticated={isAuthenticatedOrDevMode}
          isExistingComposition={!!currentCompositionId}
        />
      </section>

      {/* AI Generation Controls */}
      <section id="composition-controls" aria-label="Composition controls">
        <GenerateControls
          isAuthenticated={isAuthenticatedOrDevMode}
          currentComposition={getComposition()}
          onCompositionGenerated={handleCompositionGenerated}
          onInsufficientCredits={handleInsufficientCredits}
        />
      </section>

      {/* Template Button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
        <TemplateButton ref={templateButtonRef} onClick={handleOpenTemplateBrowser} />
      </div>

      {/* Music Sequencer Grid */}
      <section id="grid-section" aria-label="Music sequencer grid" style={{ marginBottom: '1rem' }}>
        <h2 className="sr-only">Music Composition Grid</h2>
        <div className="w-11/12 max-w-[1800px] mx-auto">
          <SequencerGrid
            gridState={gridState}
            noteState={noteState}
            volumeState={volumeState}
            muted={muted}
            instrumentNames={instrumentNames}
            instrumentParameters={instrumentParameters}
            parametersEnabled={parametersEnabled}
            effectsChains={effectsChains}
            effectsPanelExpanded={effectsPanelExpanded}
            steps={steps}
            currentStep={currentStep}
            onCellToggle={toggleCell}
            onNoteChange={setNote}
            onInstrumentChange={setInstrument}
            onParametersChange={handleParametersChange}
            onParametersEnabledChange={toggleParametersEnabled}
            onVolumeChange={handleVolumeChange}
            onMuteToggle={handleMuteToggle}
            onAddRow={addRow}
            onRemoveRow={removeRow}
            onEffectsChange={setEffectsChain}
            onEffectsPanelToggle={toggleEffectsPanel}
          />
        </div>
      </section>



      {/* Save Indicator */}
      {saveIndicator && (
        <div
          className="fixed bottom-4 right-4 font-pixel"
          style={{
            animation: 'slideInUp 0.3s ease-out',
            zIndex: 100,
          }}
          aria-live="polite"
          aria-atomic="true"
        >
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(0, 255, 0, 0.15) 0%, rgba(0, 200, 0, 0.1) 100%)',
              backdropFilter: 'blur(8px)',
              border: '1.5px solid rgba(0, 255, 0, 0.4)',
              borderRadius: '8px',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0, 255, 0, 0.2), 0 0 12px rgba(0, 255, 0, 0.1)',
            }}
          >
            <span
              style={{
                fontSize: '0.7rem',
                animation: 'pulse 0.6s ease-in-out',
              }}
            >
              ✓
            </span>
            <span
              style={{
                color: 'rgba(0, 255, 0, 0.95)',
                fontSize: '0.65rem',
                fontWeight: '500',
                letterSpacing: '0.3px',
              }}
            >
              Saved
            </span>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideInUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.8;
          }
        }
      `}</style>

      {/* Features Section for SEO */}
      <section 
        className="mx-auto px-4" 
        style={{ 
          marginTop: '3rem', 
          marginBottom: '4rem',
          width: '88%', 
          maxWidth: '1200px',
          paddingBottom: '2rem',
          background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.2) 50%, transparent 100%)',
          borderRadius: '12px',
          padding: '2rem 1.5rem'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h2 
            className="text-accent" 
            style={{ 
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}
          >
            Why Choose 8-Bit Composer?
          </h2>
          <div style={{
            width: '60px',
            height: '3px',
            background: 'var(--color-accent)',
            margin: '0 auto',
            borderRadius: '2px'
          }} />
        </div>
        <div 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.75rem',
            fontSize: '0.85rem'
          }}
        >
          {[
            {
              emoji: '🎵',
              title: 'Easy to Use',
              description: 'Intuitive grid-based interface makes creating chiptune music accessible to everyone, from beginners to professionals.',
              backEmoji: '🎹',
              backTitle: 'Fun Fact!',
              backText: 'The first 8-bit music was created in 1977 for the Atari 2600. Now you can make it in your browser! 🕹️'
            },
            {
              emoji: '🤖',
              title: 'AI-Powered',
              description: 'Generate unique 8-bit compositions using AI. Describe your vision and let our algorithm create the perfect retro soundtrack.',
              backEmoji: '🧠',
              backTitle: 'Powered by Claude!',
              backText: 'We use Claude Sonnet 3.7 to generate authentic chiptune patterns. AI meets retro gaming! 🎮✨'
            },
            {
              emoji: '💾',
              title: 'Export & Share',
              description: 'Download your creations as high-quality WAV files or share them instantly with a unique link. Perfect for game development.',
              backEmoji: '🚀',
              backTitle: 'Pro Tip!',
              backText: 'Your compositions are saved locally in your browser. Clear your cache = bye bye music! Export often! 💿'
            },
            {
              emoji: '🎮',
              title: 'Authentic Sound',
              description: 'Recreate the classic 8-bit sound of NES, Game Boy, and other retro gaming consoles with authentic waveforms and filters.',
              backEmoji: '🔊',
              backTitle: 'Nerd Alert!',
              backText: 'We use Web Audio API with custom oscillators to recreate those sweet, sweet square waves. Science! 🔬'
            },
            {
              emoji: '⚡',
              title: 'Browser-Based',
              description: 'No downloads or installations required. Create music directly in your web browser on any device, anywhere.',
              backEmoji: '🌐',
              backTitle: 'Cool Beans!',
              backText: 'This entire app runs in your browser. No servers processing your music. Your computer is the studio! 🎛️'
            },
            {
              emoji: '🆓',
              title: 'Free to Use',
              description: 'Create, edit, export, and share your compositions for free. AI generation features available with affordable credit system. No watermarks on your music.',
              backEmoji: '💝',
              backTitle: 'Thank You!',
              backText: 'Made with ❤️ and way too much coffee. If you love it, share it with your friends! ☕🎵'
            }
          ].map((card, index) => {
            const isFlipped = flippedCards.has(index);
            return (
              <div
                key={index}
                style={{
                  perspective: '1000px',
                  minHeight: '240px'
                }}
              >
                <article
                  onClick={() => {
                    setFlippedCards(prev => {
                      const newSet = new Set(prev);
                      if (newSet.has(index)) {
                        newSet.delete(index);
                      } else {
                        newSet.add(index);
                      }
                      return newSet;
                    });
                  }}
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minHeight: '240px',
                    transition: 'transform 0.6s',
                    transformStyle: 'preserve-3d',
                    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!isFlipped) {
                      e.currentTarget.style.transform = 'translateY(-6px) scale(1.02) rotateY(0deg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isFlipped) {
                      e.currentTarget.style.transform = 'translateY(0) scale(1) rotateY(0deg)';
                    }
                  }}
                >
                  {/* Front of card */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      MozBackfaceVisibility: 'hidden',
                      background: 'linear-gradient(135deg, var(--color-surface) 0%, rgba(0, 0, 0, 0.4) 100%)',
                      border: '2px solid var(--color-accent)',
                      borderRadius: '10px',
                      padding: '1.5rem',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                      transform: 'rotateY(0deg)'
                    }}
                  >
                    <div style={{ fontSize: '2.25rem', marginBottom: '0.75rem', filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3))' }}>
                      {card.emoji}
                    </div>
                    <h3 className="text-accent" style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                      {card.title}
                    </h3>
                    <p style={{ lineHeight: '1.6', opacity: 0.95, color: 'var(--color-fg)', fontSize: '0.8rem' }}>
                      {card.description}
                    </p>
                  </div>
                  
                  {/* Back of card */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      MozBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, var(--color-surface) 100%)',
                      border: '2px solid var(--color-accent)',
                      borderRadius: '10px',
                      padding: '1.25rem 1.5rem',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                    }}
                  >
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))' }}>
                      {card.backEmoji}
                    </div>
                    <h3 className="text-accent" style={{ marginBottom: '0.75rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
                      {card.backTitle}
                    </h3>
                    <p style={{ lineHeight: '1.6', opacity: 0.95, color: 'var(--color-fg)', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                      {card.backText}
                    </p>
                    <p style={{ marginTop: 'auto', fontSize: '0.65rem', opacity: 0.6, fontStyle: 'italic' }}>
                      (Click to flip back)
                    </p>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quick Facts Section - GEO Optimized */}
      <QuickFacts />
          </>
        )}

      {/* Help Modal - lazy loaded */}
      <Suspense fallback={null}>
        <HelpModal isOpen={isHelpOpen} onClose={handleCloseHelp} />
      </Suspense>

      {/* Template Browser - lazy loaded */}
      <Suspense fallback={
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            color: 'var(--color-fg)',
            fontSize: '1.5rem',
            fontFamily: 'var(--font-family)',
          }}>
            Loading templates...
          </div>
        </div>
      }>
        <TemplateBrowser
          isOpen={isTemplateBrowserOpen}
          onClose={handleCloseTemplateBrowser}
          onSelectTemplate={handleSelectTemplate}
          triggerRef={templateButtonRef}
        />
      </Suspense>

      {/* Confirmation Dialog for Unsaved Changes */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Loading a template will replace your current composition. Do you want to continue?"
        confirmText="Load Template"
        cancelText="Cancel"
        onConfirm={handleConfirmLoadTemplate}
        onCancel={handleCancelLoadTemplate}
      />

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        required={insufficientCreditsInfo.required}
        available={insufficientCreditsInfo.available}
        onClose={handleCloseInsufficientCreditsModal}
      />

      {/* Save Composition Dialog */}
      <SaveCompositionDialog
        isOpen={showSaveDialog}
        onClose={handleCloseSaveDialog}
        onSave={handleSaveComposition}
        currentTitle={compositionTitle}
        isExistingComposition={!!currentCompositionId}
        isSaving={isSaving}
      />

      {/* Composition Limit Modal */}
      <CompositionLimitModal
        isOpen={showLimitModal}
        onClose={handleCloseLimitModal}
        currentCount={compositionCount}
        subscriptionTier={sessionManager.getProfile()?.subscriptionTier || 'free'}
        onNavigateToLibrary={handleNavigateToLibrary}
      />

      {/* Discord Invite Popup - Non-invasive bottom-right popup */}
      <DiscordInvitePopup delay={5000} />
      </main>
    </>
  );
}
